import { ModelTopologyFeatureBinding } from './ModelTopologyFeatureBinding';

/**
 * 收集部分边线参数中的参考点。
 *
 * @param partialEdges 部分边线参数集合。
 * @returns 参考点集合。
 */
function collectPartialEdgeReferencePoints(partialEdges: PartialEdgeParam[] | undefined): BlendPointRef[] {
    return (partialEdges ?? []).flatMap((partialEdge) => [
        partialEdge.startReferencePoint ?? null,
        partialEdge.endReferencePoint ?? null,
    ]).filter((pointRef): pointRef is BlendPointRef => Boolean(pointRef));
}

/**
 * 解析圆角定义中的全部拓扑选择项。
 *
 * @param definition 圆角定义。
 * @returns 去重后的拓扑选择项。
 */
function collectFilletTopologyItems(definition: FilletFeatureDefinition) {
    const items = [
        ...(definition.items ?? []),
        ...(definition.boundaryItems ?? []),
        ...((definition.faceSets ?? []).flatMap((faceSet) => faceSet.items ?? [])),
    ];
    const itemByRef = new Map(items.map((item) => [item.persistentRef, item]));
    return Array.from(itemByRef.values());
}

/**
 * 解析圆角定义中的点类引用。
 *
 * @param definition 圆角定义。
 * @returns 去重后的点类引用。
 */
function collectFilletPointRefs(definition: FilletFeatureDefinition) {
    const pointRefs = [
        definition.helpPoint ?? null,
        ...collectPartialEdgeReferencePoints(definition.partialEdges),
    ].filter((pointRef): pointRef is BlendPointRef => pointRef !== null);
    const pointByRef = new Map(pointRefs.map((pointRef) => [pointRef.persistentRef ?? JSON.stringify(pointRef.point), pointRef]));
    return Array.from(pointByRef.values());
}

/**
 * 构建圆角模型拓扑绑定。
 *
 * 设计说明：
 * 1. 圆角的输入真相是模型拓扑而不是草图轮廓，因此必须收口到 `model-topology` binding；
 * 2. 这里仅负责把定义中的边、面、顶点和 help point 投影为可持久化 binding；
 * 3. 上层宿主与命令适配器统一复用该函数，避免各处重复拼 `referenceBindings`。
 *
 * @param input 构建输入。
 * @returns 可写入命令参数的绑定列表。
 */
export function buildFilletModelTopologyBindings(input: {
    upstreamBodyObjectId?: string | null;
    definition: FilletFeatureDefinition;
}): SerializedFeatureReferenceBinding[] {
    const items = collectFilletTopologyItems(input.definition);
    const pointRefs = collectFilletPointRefs(input.definition);
    const bodyObjectId = input.upstreamBodyObjectId
        ?? items[0]?.bodyObjectId
        ?? pointRefs[0]?.bodyObjectId
        ?? null;
    if (!bodyObjectId) {
        return [];
    }
    if (items.length <= 0 && pointRefs.length <= 0) {
        return [];
    }
    return [
        ModelTopologyFeatureBinding.createPayload({
            bodyObjectId,
            items,
            pointRefs,
        }),
    ];
}
